import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TableColumn } from '../../atoms/table/table.model';
import { TableComponent } from '../../atoms/table/table.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { LOSSES_TABLE_DEFAULT_COLUMNS } from '../../../constants/table.constants';
import { EventDetails, ItemLossesBalanceParams, OrderType } from '@data';
import {
  BadgeLossesIndicatorComponent,
  IndicatorAction,
  LossesIndicatorComponent,
} from '@ui';
import { PermissionsService } from '../../../../app/core/services/permissions.service';
import { NzEmptyModule } from 'ng-zorro-antd/empty';

@Component({
  selector: 'ui-losses-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TranslocoDirective,
    LossesIndicatorComponent,
    TranslocoDecimalPipe,
    BadgeLossesIndicatorComponent,
    NzEmptyModule,
  ],
  template: `
    <ng-container *transloco="let t">
      <div>
        @if (dataSource() && columns().length) {
          <ui-table
            [dataSource]="dataSource()"
            [columns]="columns()"
            [columnTemplates]="columnTemplates()"
            [scrollConfig]="scrollConfig()"
            [pageSize]="pageSize()"
            [pageIndex]="pageIndex()"
            [showPagination]="showPagination()"
            [showPageSizeChanger]="showPageSizeChanger()"
            [total]="total()"
            [showTotal]="total() ? totalTemplate : null"
            [loading]="loading()"
            [frontPagination]="false"
            [isMultiSelect]="false"
            (onQueryParams)="onQueryParamsChange($event)"
          />
        } @else if (!loading()) {
          <nz-empty
            nzNotFoundImage="simple"
            [nzNotFoundContent]="emptyContentTemplate"
            class="py-8"
          ></nz-empty>
        }

        <ng-template #emptyContentTemplate>
          <span style="color: #737373; font-size: 0.875rem;">{{
            t('CHARTS.VOID_TABLE_MESSAGE')
          }}</span>
        </ng-template>

        <ng-template #centerTemplate let-row="row">
          <!-- cambiar -->
          <ui-losses-indicator
            [state]="row.dateEnd !== null ? 'active' : 'danger'"
            [centerName]="row.centerName"
            [centerId]="row.centerId"
            [distributorId]="row.distributorId"
            [rsabtId]="row.rsabtId"
            [tsabtId]="row.tsabtId"
            [vtnId]="row.vtnId"
            (onCenter)="emitCenter($event)"
          />
        </ng-template>
        <ng-template #hoursPercentageTemplate let-row="row">
          <ui-badge-losses-indicator
            [value]="row.hoursPercentage"
            [units]="'%'"
          />
        </ng-template>
        <ng-template #lossesTemplate let-row="row">
          <ui-badge-losses-indicator
            [value]="row.losses"
            [percentage]="row.lossesPercentage"
            [units]="row.lossesUnit"
            [backgroundColor]="'bg-amber-50'"
          />
        </ng-template>
        <ng-template #energyCTTemplate let-row="row">
          <ui-badge-losses-indicator
            [value]="row.energyCT"
            [units]="row.ctEnergyUnit"
          />
        </ng-template>
        <ng-template #consumptionTemplate let-row="row">
          <ui-badge-losses-indicator
            [value]="row.consumption"
            [units]="row.consumptionUnit"
          />
        </ng-template>
        <ng-template #totalTemplate>
          {{ total() | translocoDecimal }}
          {{ t('DISTRIBUTION_NETWORK.TABLE.RESULTS') }}
        </ng-template>
      </div>
    </ng-container>
  `,
})
export class LossesTableComponent {
  private translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  protected readonly permissionsService = inject(PermissionsService);

  icon = signal<string>('line-chart');
  iconClass = signal(
    'text-yellow-500 bg-yellow-50 shadow-custom-orange me-[16px]',
  );

  data = input.required<ItemLossesBalanceParams[]>();
  total = input<number>(100);
  loading = input<boolean>(false);
  pageSize = input<number>(10);
  pageIndex = input<number>(1);
  showPagination = input<boolean>(false);
  showPageSizeChanger = input<boolean>(false);
  showCenterCode = input<boolean>(false);

  onQueryParams = output<{
    orderBy: string | undefined;
    order: OrderType | undefined;
    pageSize: number;
    pageIndex: number;
  }>();
  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }>();
  onCopyCenterId = output<string>();
  onRsabtId = output<string>();
  onAction = output<IndicatorAction>();
  onChecked = output<EventDetails[]>();

  actions = signal<Record<any, { disabled: boolean }>>({
    archive: { disabled: false },
  });

  dataSource = computed(() =>
    (this.data() ?? []).map((item) => {
      const stableId =
        (item as any).id ??
        `${item.centerId}_${item.distributorId}_${item.rsabtId}`;
      const rawPercentage =
        item.energyCT > 0 ? (item.losses / item.energyCT) * 100 : 0;
      const percentageLosses = Number.isFinite(rawPercentage)
        ? Math.round(rawPercentage * 100) / 100
        : 0;
      return { ...item, id: stableId, percentage: percentageLosses };
    }),
  );

  centerTemplate = viewChild.required<TemplateRef<any>>('centerTemplate');
  hoursPercentageTemplate = viewChild.required<TemplateRef<any>>(
    'hoursPercentageTemplate',
  );
  lossesTemplate = viewChild.required<TemplateRef<any>>('lossesTemplate');
  energyCTTemplate = viewChild.required<TemplateRef<any>>('energyCTTemplate');
  consumptionTemplate = viewChild.required<TemplateRef<any>>(
    'consumptionTemplate',
  );

  columnTemplates = computed(() => ({
    center: this.centerTemplate(),
    hoursPercentage: this.hoursPercentageTemplate(),
    losses: this.lossesTemplate(),
    energyCT: this.energyCTTemplate(),
    consumption: this.consumptionTemplate(),
  }));

  columns = signal<TableColumn[]>([]);
  scrollConfigDefault = signal<{ x?: string | null; y?: string | null }>({
    x: '800px',
    y: '400px',
  });
  scrollConfig = computed<{ x?: string | null; y?: string | null }>(() =>
    this.data()?.length ? this.scrollConfigDefault() : {},
  );
  DEFAULT_COLUMNS = computed<TableColumn[]>(() => {
    return LOSSES_TABLE_DEFAULT_COLUMNS;
  });

  constructor() {
    effect(() => {
      this.setLocalizeColumns();
    });
  }

  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS().map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { sort, pageSize, pageIndex } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const orderBy = (currentSort && currentSort.key) || undefined;
    const order: string | undefined =
      (currentSort && currentSort.value) || undefined;
    this.onQueryParams.emit({
      orderBy,
      order: this.getOrder(order),
      pageSize,
      pageIndex,
    });
  }

  private getOrder(order: string | undefined): OrderType | undefined {
    if (order === 'ascend') return 'asc';
    if (order === 'descend') return 'desc';
    return undefined;
  }

  emitCenter(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }) {
    this.onCenter.emit(event);
  }
}
